// searchModel — the pure read-model behind the Library (Bibliotek): the row
// shape the host projects (searchProjection.ts) and the one ranking that turns
// the query text + the table's sort into the rows the surface shows.
//
// THE LIBRARY IS A TABLE (owner, D23 2026-10-06): one row per resource under
// named columns, sorted by the column the author clicked (title ascending by
// default). Free text keeps the rows that match and groups them by the FIELD
// they matched in (title, then author), each group in the table's order, so
// "why is this here" stays visible. Content hits (C3, the server's full-text
// search) come after, in their own group (contentSearch.ts).
//
// The query language (modifiers, folding, prefix + one-typo matching) is
// libraryQuery.ts. This module owns NO display strings: closed enums and a
// `Recency` sum; the view localizes at the render edge.

import type { MaterialTemplate } from "../../app/ports.js";
import type { SourceReadStatus } from "../../app/sourceReadStatus.js";
import type { ExportFormatKind } from "../ExportView/ExportView.js";
import {
    applyFilters,
    type FreeTerm,
    freeTermOf,
    freeTermsOf,
    freeWordsOf,
    matchesIn,
    termHits,
    tokenize,
    type WordMatch,
} from "./libraryQuery.js";

// Freshness bucket — computed from a resource's `lastEditedAt` against the wall
// clock (searchProjection.freshnessOf), NEVER inside this module.
export type FreshnessBucket = "today" | "yesterday" | "this-week" | "earlier";

// A structured relative-time value — a sum, not a pre-baked string, so the
// component renders it in the viewer's locale. `raw` carries an upstream label
// (a conversation's `when`), passed through untouched.
export type Recency =
    | { readonly type: "now" }
    | { readonly type: "minutes"; readonly n: number }
    | { readonly type: "hours"; readonly n: number }
    | { readonly type: "yesterday" }
    | { readonly type: "days"; readonly n: number }
    | { readonly type: "date"; readonly day: number; readonly month: number } // month 0-11
    | { readonly type: "raw"; readonly text: string }
    | { readonly type: "unknown" };

// The five searchable entity kinds — the workspace's flat pool. A closed union;
// a new kind widens it and every match site breaks until it is placed.
export type SearchResultKind =
    | "source"
    | "material"
    | "package"
    | "file"
    | "conversation";

const SEARCH_KINDS: readonly SearchResultKind[] = [
    "source",
    "material",
    "package",
    "file",
    "conversation",
];

// One searchable entity, already projected from the Library. `meta` is a single
// precomputed supporting fragment (the format tag); `location` is the workspace.
export type SearchResult = {
    readonly kind: SearchResultKind;
    readonly id: string;
    readonly title: string;
    readonly location: string;
    readonly recency: Recency;
    readonly freshnessBucket: FreshnessBucket;
    readonly meta: string;
    // F86 — the resource is archived (set aside). Absent ⇒ active. Only a
    // `status:archived` query shows archived rows (libraryQuery.ts).
    readonly archived?: true;
    // A source's "authors · year" (e.g. "Bird & de Moor · 1997").
    readonly byline?: string;
    // A source's full author names (the `author:` filter + author search).
    readonly authors?: readonly string[];
    // Creation moment, ISO 8601 (the `created:` filter).
    readonly createdAt?: string;
    // How many live materials derive from it (the row's "i N material").
    readonly usedIn?: number;
    // Last touched, ISO 8601 (the "Edited" column and its sort).
    readonly editedAt?: string;
    readonly facts?: RowFacts;
    // A material the agent authored (FR-2.7): the row shows the AI mark.
    readonly aiAuthored?: true;
    // A source still being read in, or that failed to read (FR-3.3/3.6).
    // Absent once it is readable.
    readonly readStatus?: SourceReadStatus;
};

// What a material or package row says it is beyond its kind, so a material
// and the package made from it never read as duplicates. Data, localized by
// the view: a material's template, sources and versions; a package's format
// and the material it was made from.
type MaterialFacts = {
    readonly type: "material";
    readonly template?: MaterialTemplate;
    readonly basedOn: readonly string[];
    readonly versions?: number;
};
type PackageFacts = {
    readonly type: "package";
    readonly format: ExportFormatKind;
    readonly from?: string;
};
export type RowFacts = MaterialFacts | PackageFacts;

export type SearchFacet = SearchResultKind;
export const SEARCH_FACETS: readonly SearchFacet[] = SEARCH_KINDS;

// === Sort ===================================================================
// The table's columns and the order they sort in: a per-DEVICE UI preference
// (app/librarySortPreference.ts), never a domain command. Title ascending is
// the default. Multi-column sort is a later todo.
export type SortColumn =
    | "title"
    | "kind"
    | "author"
    | "based-on"
    | "used-in"
    | "edited";
export const SORT_COLUMNS: readonly SortColumn[] = [
    "title",
    "kind",
    "author",
    "based-on",
    "used-in",
    "edited",
];
export type SortDirection = "ascending" | "descending";
export type LibrarySort = {
    readonly column: SortColumn;
    readonly direction: SortDirection;
};
// The Library opens on what changed last (owner decision 2026-10-19, as in
// the design).
export const DEFAULT_SORT: LibrarySort = {
    column: "edited",
    direction: "descending",
};

// === Results ================================================================

export type TextSegment = { readonly text: string; readonly matched: boolean };

export type CompactRow = {
    readonly result: SearchResult;
    readonly titleSegments: readonly TextSegment[];
    // The byline with an author match highlighted (free-text author hits).
    readonly bylineSegments?: readonly TextSegment[];
};

// Browsing: one group, every row. Free text: a group per field the resource
// matched in (weight order). Kind: browsing everything, the materials made
// here, then the sources under their own heading (sources live in the Library
// until the Knowledge bank exists, kept apart from what the author made).
type GroupAxis = "all" | "field" | "kind";
type MatchField = "title" | "author";
const MATCH_FIELDS: readonly MatchField[] = ["title", "author"];

// A group's `key` is "all", a field, or "made" / "sources"; the component
// labels it.
type LibraryGroup = {
    readonly key: string;
    readonly rows: readonly CompactRow[];
};

// "brid" -> "Bird": shown only when EVERY hit needed the one-typo tolerance.
type Correction = { readonly typed: string; readonly matched: string };

export type LibraryResults = {
    readonly axis: GroupAxis;
    readonly groups: readonly LibraryGroup[];
    readonly correction?: Correction;
};

// The ranked list plus what the surface needs beside it: the filtered pool (the
// content hits are kept to it) and the words the content search runs, one
// search each (a typo-corrected word as corrected, so the content agrees with
// the note above the list).
export type LibraryRanking = {
    readonly results: LibraryResults;
    readonly pool: readonly SearchResult[];
    readonly contentWords: readonly string[];
};

const collator = new Intl.Collator("sv", {
    sensitivity: "base",
    numeric: true,
});

const byTitle = (a: SearchResult, b: SearchResult): number =>
    collator.compare(a.title, b.title);

// A column's text for sorting: the byline, what it is based on.
const basedOnOf = (r: SearchResult): string =>
    r.facts === undefined
        ? ""
        : r.facts.type === "material"
          ? (r.facts.basedOn[0] ?? "")
          : (r.facts.from ?? "");

// Compare by one column's value in ascending order. An empty value sorts last
// whatever the direction (it says nothing); ties fall back to the title.
const columnCompare = (
    column: SortColumn,
    a: SearchResult,
    b: SearchResult,
): number | undefined => {
    const text = (x: string, y: string): number | undefined =>
        x === "" && y === ""
            ? 0
            : x === ""
              ? undefined
              : y === ""
                ? undefined
                : collator.compare(x, y);
    switch (column) {
        case "title":
            return byTitle(a, b);
        case "kind":
            return SEARCH_KINDS.indexOf(a.kind) - SEARCH_KINDS.indexOf(b.kind);
        case "author":
            return text(a.byline ?? "", b.byline ?? "");
        case "based-on":
            return text(basedOnOf(a), basedOnOf(b));
        case "used-in":
            return (a.usedIn ?? 0) - (b.usedIn ?? 0);
        case "edited":
            return text(a.editedAt ?? "", b.editedAt ?? "");
    }
};

const emptyOf = (column: SortColumn, r: SearchResult): boolean => {
    switch (column) {
        case "author":
            return (r.byline ?? "") === "";
        case "based-on":
            return basedOnOf(r) === "";
        case "edited":
            return (r.editedAt ?? "") === "";
        case "title":
        case "kind":
        case "used-in":
            return false;
    }
};

// The table's order for a sort.
export const compareBy =
    (sort: LibrarySort) =>
    (a: SearchResult, b: SearchResult): number => {
        const ea = emptyOf(sort.column, a);
        const eb = emptyOf(sort.column, b);
        if (ea !== eb) return ea ? 1 : -1;
        const sign = sort.direction === "ascending" ? 1 : -1;
        const c = columnCompare(sort.column, a, b) ?? 0;
        return c !== 0 ? sign * c : byTitle(a, b);
    };

const plain = (result: SearchResult): CompactRow => ({
    result,
    titleSegments: [{ text: result.title, matched: false }],
});

// Split `text` at the matched spans into marked/unmarked runs.
const segmentsOf = (
    text: string,
    matches: readonly WordMatch[],
): readonly TextSegment[] => {
    const cuts = [...matches]
        .sort((a, b) => a.start - b.start)
        .reduce<{ readonly at: number; readonly out: readonly TextSegment[] }>(
            (acc, m) =>
                m.start < acc.at
                    ? acc
                    : {
                          at: m.end,
                          out: [
                              ...acc.out,
                              {
                                  text: text.slice(acc.at, m.start),
                                  matched: false,
                              },
                              {
                                  text: text.slice(m.start, m.end),
                                  matched: true,
                              },
                          ],
                      },
            { at: 0, out: [] },
        );
    return [...cuts.out, { text: text.slice(cuts.at), matched: false }].filter(
        (s) => s.text !== "",
    );
};

// Browsing (no free text): every row, in the table's order.
const browse = (
    sort: LibrarySort,
    pool: readonly SearchResult[],
): LibraryResults => ({
    axis: "all",
    groups: [{ key: "all", rows: [...pool].sort(compareBy(sort)).map(plain) }],
});

type Hit = {
    readonly row: CompactRow;
    readonly field: MatchField;
    // The matched word, when every match needed a typo.
    readonly typo?: string;
};

// One resource against the free terms: every term must hit the title or an
// author; its field is the best one any term hit.
const hitOf = (
    result: SearchResult,
    terms: readonly FreeTerm[],
): Hit | undefined => {
    const authors = result.authors ?? [];
    const byline = result.byline ?? "";
    const inTitle = terms.filter((t) => termHits(result.title, t));
    const inAuthor = terms.filter(
        (t) => termHits(byline, t) || authors.some((a) => termHits(a, t)),
    );
    if (!terms.every((t) => inTitle.includes(t) || inAuthor.includes(t)))
        return undefined;
    const titleMatches = matchesIn(result.title, inTitle);
    const bylineMatches = inAuthor.flatMap((t) => matchesIn(byline, [t]));
    const all = [...titleMatches, ...bylineMatches];
    const firstTypo = all.every((m) => m.typo) ? all[0] : undefined;
    const typoSource = titleMatches.length > 0 ? result.title : byline;
    return {
        row: {
            result,
            titleSegments: segmentsOf(result.title, titleMatches),
            ...(bylineMatches.length > 0
                ? { bylineSegments: segmentsOf(byline, bylineMatches) }
                : {}),
        },
        field: inTitle.length > 0 ? "title" : "author",
        ...(firstTypo !== undefined
            ? { typo: typoSource.slice(firstTypo.start, firstTypo.end) }
            : {}),
    };
};

// Searching (free text): one row per resource under its best field.
const search = (
    terms: readonly FreeTerm[],
    sort: LibrarySort,
    pool: readonly SearchResult[],
): LibraryResults => {
    const hits = [...pool]
        .sort(compareBy(sort))
        .flatMap((r) => hitOf(r, terms) ?? []);
    const first = hits[0];
    const typed = terms.find((t) => t.type === "text")?.value ?? "";
    const correction =
        first?.typo !== undefined && hits.every((h) => h.typo !== undefined)
            ? { typed, matched: first.typo }
            : undefined;
    return {
        axis: "field",
        groups: MATCH_FIELDS.map((field) => ({
            key: field,
            rows: hits.filter((h) => h.field === field).map((h) => h.row),
        })).filter((g) => g.rows.length > 0),
        ...(correction !== undefined ? { correction } : {}),
    };
};

// The ONE ranking: the query text (filters + free text) over the pool, in the
// browse order. Pure, deterministic — safe in a render via `useMemo`.
export const rankLibrary = (
    query: string,
    sort: LibrarySort,
    pool: readonly SearchResult[],
): LibraryRanking => {
    const tokens = tokenize(query);
    const filtered = applyFilters(tokens, pool);
    const terms = freeTermsOf(tokens);
    const results =
        terms.length === 0
            ? browse(sort, filtered)
            : search(terms, sort, filtered);
    const { correction } = results;
    return {
        results,
        pool: filtered,
        contentWords: freeWordsOf(tokens).map((word) =>
            correction !== undefined &&
            freeTermOf(word).value === correction.typed
                ? correction.matched
                : word,
        ),
    };
};

// The rows in list order (keyboard + prev/next walk this).
export const rowsOf = (results: LibraryResults): readonly CompactRow[] =>
    results.groups.flatMap((g) => g.rows);

// Browsing everything: the sources go under their own heading after the rest
// (the materials, their packages, files). Only when both sides have rows;
// free-text (field) groups and single-kind lists stay as ranked.
export const separateSources = (results: LibraryResults): LibraryResults => {
    if (results.axis !== "all") return results;
    const rows = results.groups.flatMap((g) => g.rows);
    const sources = rows.filter((row) => row.result.kind === "source");
    const made = rows.filter((row) => row.result.kind !== "source");
    if (sources.length === 0 || made.length === 0) return results;
    return {
        ...results,
        axis: "kind",
        groups: [
            { key: "made", rows: made },
            { key: "sources", rows: sources },
        ],
    };
};
